import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { Typography } from '@mui/material';
import * as Constants from 'common/Constants';
import { useSelector } from 'react-redux';
import store from 'app/store';
import CancelScenarioDialog from './cancelScenarioDialog';
import { updateScenarioSection, updateCoveragePage, updateFormState } from '../state/reducer';

export default function VerticalStepper() {
  const accidentObject = useSelector((state) => state.scenario.accidentObject);
  const criticalIllnessObject = useSelector((state) => state.scenario.criticalIllnessObject);
  const hospitalIndemnityObject = useSelector((state) => state.scenario.hospitalIndemnityObject);
  const currPage = useSelector((state) => state.scenario.scenarioSection);
  const currCoveragePage = useSelector((state) => state.scenario.coveragePage);
  const scenarioId = useSelector((state) => state.scenario.scenarioId);
  const processStatus = useSelector((state) => state.scenario.processStatus);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const formStateValue = useSelector((state) => state.scenario.isModified);
  const [dialogIndex, setDialogIndex] = React.useState(null);
  const [dialogSubindex, setDialogSubindex] = React.useState(null);
  const HIProductIncluded = useSelector((state) => state.scenario.includeProductHI);
  const CIProductIncluded = useSelector((state) => state.scenario.includeProductCI);
  const ACProductIncluded = useSelector((state) => state.scenario.includeProductAC);
  const singleProductSelected = HIProductIncluded || CIProductIncluded || ACProductIncluded;
  const carrierSteps = useSelector((state) => state.auth.steps);
  const HIPayoutDetail = useSelector(
    (state) => state.scenario.HIPayoutDetail,
  );
  const CIPayoutDetail = useSelector(
    (state) => state.scenario.CIPayoutDetail,
  );
  const ACPayoutDetail = useSelector(
    (state) => state.scenario.ACPayoutDetail,
  );
  const atLeastoneHIChecked = HIPayoutDetail
    .filter((benefit) => hospitalIndemnityObject[`${benefit?.name}Checked`])
    .filter((benefit) => benefit?.code !== null).length > 0;
  const atLeastoneCIChecked = CIPayoutDetail
    .filter((benefit) => criticalIllnessObject[`${benefit?.name}Checked`])
    .filter((benefit) => benefit?.code !== null).length > 0;
  const atLeastoneACChecked = ACPayoutDetail
    .filter((benefit) => accidentObject[`${benefit?.name}Checked`])
    .filter((benefit) => benefit?.code !== null).length > 0;
  const effectiveHISelected = HIProductIncluded ? atLeastoneHIChecked : false;
  const effectiveCISelected = CIProductIncluded ? atLeastoneCIChecked : false;
  const effectiveACSelected = ACProductIncluded ? atLeastoneACChecked : false;
  const atLeastOneBenefitSelected = singleProductSelected && (
    effectiveHISelected || effectiveCISelected || effectiveACSelected);

  const redirect = (toPage) => {
    if (toPage !== currPage) {
      store.dispatch(updateScenarioSection(toPage));
    }
  };

  const cancelScenarioClose = () => {
    setCancelDialogOpen(false);
  };

  const getEnabled = (index, subIndex) => {
    if ((subIndex === 0 && !HIProductIncluded) || (subIndex === 1 && !CIProductIncluded)
      || (subIndex === 2 && !ACProductIncluded) || (!scenarioId && index)
      || (processStatus !== Constants.STATUS_CODE.SUCCESS && index)) {
      return 'grey';
    }
    if (currPage === index && currCoveragePage === subIndex) {
      return 'primary.dark';
    }

    return 'black';
  };

  const getEnabledStep = (index) => {
    const code = carrierSteps[index]?.code;
    if ((code === 'COMPARE_PLANS' && !atLeastOneBenefitSelected)
        || (!scenarioId && index)
        || (processStatus !== Constants.STATUS_CODE.SUCCESS && index)) {
      return 'grey';
    }
    if (currPage === index) {
      return 'primary.dark';
    }
    return 'black';
  };

  const handleCoverageStepClick = (index, subindex, screen) => {
    if ((subindex === 0 && HIProductIncluded) || (subindex === 1 && CIProductIncluded)
    || (subindex === 2 && ACProductIncluded)) {
      if ((screen || !formStateValue) && scenarioId && scenarioId > 0
        && processStatus === Constants.STATUS_CODE.SUCCESS) {
        store.dispatch(updateCoveragePage(subindex));
        redirect(index);
        store.dispatch(updateFormState(false));
      }
      if (formStateValue && scenarioId && scenarioId > 0) {
        setCancelDialogOpen(formStateValue);
        setDialogIndex(index);
        setDialogSubindex(subindex);
      }
    }
  };

  const redirectCoverageStep = (index, screen) => {
    if (index === 3 && HIProductIncluded) {
      handleCoverageStepClick(index, 0, screen);
      return;
    }
    if (index === 3 && CIProductIncluded) {
      handleCoverageStepClick(index, 1, screen);
      return;
    }
    if (index === 3 && ACProductIncluded) {
      handleCoverageStepClick(index, 2, screen);
    }
  };

  const handleStepClick = (index, screen) => {
    const code = carrierSteps[index]?.code;
    if ((index === 3 || code === 'COMPARE_PLANS') && !atLeastOneBenefitSelected) {
      return;
    }
    redirectCoverageStep(index, screen);
    if ((screen || !formStateValue) && scenarioId > 0
    && processStatus === Constants.STATUS_CODE.SUCCESS) {
      redirect(index);
      store.dispatch(updateFormState(false));
    }
    if (formStateValue && scenarioId && scenarioId > 0) {
      setCancelDialogOpen(formStateValue);
      setDialogIndex(index);
    }
  };

  return (
    <Box>
      <Stepper
        nonLinear
        orientation="vertical"
        connector={null}
        activeStep={currPage}
      >
        {carrierSteps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>
              <Typography
                fontWeight={currPage === index ? 'bold' : 'normal'}
                color={getEnabledStep(index)}
                onClick={() => handleStepClick(index)}
                sx={{ cursor: getEnabledStep(index) === 'grey' ? 'default' : 'pointer' }}
              >
                {step.label}
              </Typography>
            </StepLabel>
            {step.sublabel ? step.sublabel.map((substep, subindex) => (
              <Typography
                fontWeight={currPage === index && currCoveragePage === subindex ? 'bold' : 'normal'}
                color={getEnabled(index, subindex)}
                onClick={() => handleCoverageStepClick(index, subindex)}
                sx={{ cursor: getEnabled(index, subindex) === 'grey' ? 'default' : 'pointer', marginLeft: '32px' }}
              >
                {substep}
              </Typography>
            )) : null}
          </Step>
        ))}
      </Stepper>
      <CancelScenarioDialog
        formState={formStateValue}
        isOpen={cancelDialogOpen}
        onClose={() => cancelScenarioClose(false)}
        handleStepClick={handleStepClick}
        handleCoverageStepClick={handleCoverageStepClick}
        index={dialogIndex}
        subindex={dialogSubindex}
      />
    </Box>
  );
}
